import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { PCacheable } from 'ts-cacheable';
import { RsjPaymentDataDto } from '../../beneficiaries/rsj/dto/rsj-payment-data.dto';
import { RsjPaymentInformationDto } from '../../beneficiaries/rsj/dto/rsj-payment-information.dto';
import { RsjDto } from '../../beneficiaries/rsj/dto/rsj.dto';
import { RsjInstructionDisplayDto } from '../../beneficiaries/rsj/rsj-instruction/dto/rsj-instruction-display.dto';
import { RsjInstructionNonRenewalDto } from '../../beneficiaries/rsj/rsj-instruction/dto/rsj-instruction-non-renewal.dto';
import { RsjInstructionDto } from '../../beneficiaries/rsj/rsj-instruction/dto/rsj-instruction.dto';
import { environment } from '../../environments/environment';
import { DisplayLabel } from '../models/displayLabel';
import { DocumentTitle } from '../models/enums/document-title.enum';
import { Pagination } from '../pagination/pagination';
import { PaginationOptions } from '../pagination/pagination-options';
import { TraceabilityDto } from '../traceability/traceability.dto';
import { ApiService, FileResponse } from './api.service';
import { FileService } from './file.service';

@Injectable({
  providedIn: 'root',
})
export class RSJService {
  private readonly instructionRSJCreate: Subject<RsjInstructionDto> = new Subject<RsjInstructionDto>();
  public instructionRSJCreated$: Observable<RsjInstructionDto> = this.instructionRSJCreate.asObservable();

  private rsj: Subject<RsjDto> = new Subject<RsjDto>();
  public rsj$: Observable<RsjDto> = this.rsj.asObservable();

  constructor(
    private readonly api: ApiService,
    private readonly fileService: FileService,
  ) {}

  public async getRSJ(beneficiaryId: number): Promise<void> {
    const rsj: RsjDto = await this.api.get<RsjDto>(environment.api.rsj + '/' + beneficiaryId + '/details');
    this.rsj.next(RsjDto.fromJson(rsj));
  }

  public async saveRSJ(rsj: RsjDto): Promise<void> {
    await this.api.post<void, RsjDto>(environment.api.rsj, rsj);
    this.rsj.next(rsj);
  }

  public async getPaymentData(beneficiaryId: number): Promise<RsjPaymentDataDto> {
    const payment: RsjPaymentDataDto = await this.api.get<RsjPaymentDataDto>(
      environment.api.paymentRsj + '/payment-data/' + beneficiaryId + '/details',
    );
    return RsjPaymentDataDto.fromJson(payment) || new RsjPaymentDataDto();
  }

  public async getPayments(beneficiaryId: number, options: PaginationOptions): Promise<Pagination<RsjPaymentInformationDto>> {
    return this.api.get<Pagination<RsjPaymentInformationDto>>(options.buildUrl(environment.api.paymentRsj + '/payment/' + beneficiaryId));
  }

  public async updatePaymentInfoStatus(id: number): Promise<void> {
    await this.api.post<void, any>(environment.api.paymentRsj + '/payment-data/change-status', { id });
  }

  public async savePaymentData(payment: RsjPaymentDataDto): Promise<void> {
    await this.api.post<void, RsjPaymentDataDto>(environment.api.paymentRsj + '/payment-data', payment);
  }

  public async findInstructionsByBeneficiaryId(id: number, options: PaginationOptions): Promise<Pagination<RsjInstructionDisplayDto>> {
    return this.api.getPaginated<RsjInstructionDisplayDto>(options.buildUrl(environment.api.instructionRsj + '/' + id));
  }

  public async getInstructionRSJInit(beneficiaryId: number): Promise<RsjInstructionDto> {
    return this.api.get<RsjInstructionDto>(environment.api.instructionRsj + '/' + beneficiaryId + '/init');
  }

  public async getInstructionRSJ(instructionId: number): Promise<RsjInstructionDto> {
    return this.api.get<RsjInstructionDto>(environment.api.instructionRsj + '/' + instructionId + '/details');
  }

  public async getDuplicateInstructionRSJ(instructionId: number): Promise<RsjInstructionDto> {
    return this.api.get<RsjInstructionDto>(environment.api.instructionRsj + '/' + instructionId + '/duplicate');
  }

  public async deleteInstructionRSJ(instructionId: number): Promise<void> {
    await this.api.get<void>(environment.api.instructionRsj + '/' + instructionId + '/del');
    this.instructionRSJCreate.next(undefined);
  }

  public async saveInstructionRSJ(instructionRSJ: RsjInstructionDto, emitEvent: boolean = true): Promise<void> {
    await this.api.post<void, RsjInstructionDto>(environment.api.instructionRsj, instructionRSJ);
    if (emitEvent) {
      this.instructionRSJCreate.next(instructionRSJ);
    }
  }

  public async generateInstructionRsjDocument(instructionRsjId: number, titleId: DocumentTitle, comment?: string): Promise<void> {
    const { filename, data }: FileResponse = await this.api.postWithFileResponse(
      environment.api.instructionRsj + '/' + instructionRsjId + '/document',
      { titleId, comment },
    );
    return this.fileService.saveAs(data, filename);
  }

  public async updateStatus(traceabilityDto: TraceabilityDto): Promise<void> {
    await this.api.post(environment.api.instructionRsj + '/status', traceabilityDto);
    this.instructionRSJCreate.next(undefined);
  }

  @PCacheable()
  public async findSituations(): Promise<DisplayLabel[]> {
    return this.api.get<DisplayLabel[]>(environment.api.instructionRsj + '/situations');
  }

  @PCacheable()
  public async findMobilizedPlans(): Promise<DisplayLabel[]> {
    return this.api.get<DisplayLabel[]>(environment.api.instructionRsj + '/mobilized-plans');
  }

  @PCacheable()
  public async findNonRenewalReasons(): Promise<RsjInstructionNonRenewalDto[]> {
    return this.api.get<RsjInstructionNonRenewalDto[]>(environment.api.instructionRsj + '/non-renewal');
  }

  @PCacheable()
  public async findUseTitles(): Promise<DisplayLabel[]> {
    return this.api.get<DisplayLabel[]>(environment.api.instructionRsj + '/use-title');
  }

  @PCacheable()
  public async findStateTitles(): Promise<DisplayLabel[]> {
    return this.api.get<DisplayLabel[]>(environment.api.instructionRsj + '/state-title');
  }

  @PCacheable()
  public async findRsjStates(): Promise<DisplayLabel[]> {
    return this.api.get<DisplayLabel[]>(environment.api.rsj + '/states');
  }

  @PCacheable()
  public async findRsjReasons(): Promise<DisplayLabel[]> {
    return this.api.get<DisplayLabel[]>(environment.api.rsj + '/reasons');
  }
}
